import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const INSTALL_PROMPT_STORAGE_KEY = "serento_install_prompt_shown";
const INSTALL_DISMISSED_KEY = "serento_install_dismissed";

export const useInstallPrompt = () => {
  const { user } = useAuth();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [canShowPrompt, setCanShowPrompt] = useState(false);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  // Check if user has dismissed the prompt before
  const hasBeenDismissed = useCallback(() => {
    return localStorage.getItem(INSTALL_DISMISSED_KEY) === "true";
  }, []);

  // Check if user has already seen the prompt
  const hasBeenShown = useCallback(() => {
    return localStorage.getItem(INSTALL_PROMPT_STORAGE_KEY) === "true";
  }, []);

  // Mark prompt as shown
  const markAsShown = useCallback(() => {
    localStorage.setItem(INSTALL_PROMPT_STORAGE_KEY, "true");
  }, []);

  // Mark prompt as dismissed
  const markAsDismissed = useCallback(() => {
    localStorage.setItem(INSTALL_DISMISSED_KEY, "true");
    setShowInstallBanner(false);
  }, []);

  // Check if user has created their first task
  const checkFirstTaskCreated = useCallback(async (): Promise<boolean> => {
    if (!user) return false;
    
    try {
      const { count, error } = await supabase
        .from("tasks")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id);
      
      if (error) return false;
      return (count || 0) >= 1;
    } catch {
      return false;
    }
  }, [user]);

  // Check if user has completed onboarding
  const checkOnboardingCompleted = useCallback(async (): Promise<boolean> => {
    if (!user) return false;
    
    try {
      const { data, error } = await supabase
        .from("user_preferences")
        .select("onboarding_completed")
        .eq("user_id", user.id)
        .single();
      
      if (error) return false;
      return data?.onboarding_completed === true;
    } catch {
      return false;
    }
  }, [user]);

  // Check if we should show the install prompt
  const checkCanShowPrompt = useCallback(async () => {
    // Never show if already installed or dismissed
    if (isInstalled || hasBeenDismissed()) {
      setCanShowPrompt(false);
      return;
    }

    // Check conditions: first task created OR onboarding completed
    const [hasFirstTask, hasOnboarded] = await Promise.all([
      checkFirstTaskCreated(),
      checkOnboardingCompleted(),
    ]);

    const shouldShow = hasFirstTask || hasOnboarded;
    setCanShowPrompt(shouldShow);
    
    // Show banner if conditions met and we have a deferred prompt
    if (shouldShow && deferredPrompt && !hasBeenShown()) {
      setShowInstallBanner(true);
      markAsShown();
    }
  }, [
    isInstalled,
    hasBeenDismissed,
    checkFirstTaskCreated,
    checkOnboardingCompleted,
    deferredPrompt,
    hasBeenShown,
    markAsShown,
  ]);

  // Listen for install prompt and install events
  useEffect(() => {
    // Check if already installed
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
      return;
    }

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    // Listen for successful install
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setShowInstallBanner(false);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  // Check conditions when user or deferred prompt changes
  useEffect(() => {
    if (user && deferredPrompt) {
      checkCanShowPrompt();
    }
  }, [user, deferredPrompt, checkCanShowPrompt]);

  // Trigger the install prompt
  const triggerInstallPrompt = useCallback(async () => {
    if (!deferredPrompt) return false;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === "accepted") {
        setIsInstalled(true);
        setShowInstallBanner(false);
        return true;
      }
      
      setDeferredPrompt(null);
      return false;
    } catch (error) {
      console.error("Error triggering install prompt:", error);
      return false;
    }
  }, [deferredPrompt]);

  // Force check (useful after task creation or onboarding)
  const recheckInstallEligibility = useCallback(() => {
    checkCanShowPrompt();
  }, [checkCanShowPrompt]);

  return {
    isInstalled,
    canShowPrompt,
    showInstallBanner,
    deferredPrompt: !!deferredPrompt,
    triggerInstallPrompt,
    dismissInstallBanner: markAsDismissed,
    recheckInstallEligibility,
  };
};
